import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faChartLine } from "@fortawesome/pro-solid-svg-icons";
import { SortableDataTable } from "@perfectserve/library";
import SpeedToCare from "./SpeedToCare";
import InteractionDetailModal from "./InteractionDetailModal";
import CallFlowAnalysisModal from "./CallFlowAnalysisModal";
const CallFinderResults = ({ interactions, loading, className = "", showLoadMore = false, onLoadMore, isLoadingMore = false, }) => {
    const [selectedInteraction, setSelectedInteraction] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedAnalysisInteraction, setSelectedAnalysisInteraction] = useState(null);
    const [showAnalysisModal, setShowAnalysisModal] = useState(false);
    const handleViewInteractionDetail = (interaction) => {
        setSelectedInteraction(interaction);
        setShowDetailModal(true);
    };
    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setSelectedInteraction(null);
    };
    const handleViewCallFlowAnalysis = (interaction) => {
        setSelectedAnalysisInteraction(interaction);
        setShowAnalysisModal(true);
    };
    const handleCloseAnalysisModal = () => {
        setShowAnalysisModal(false);
        setSelectedAnalysisInteraction(null);
    };
    const formatDateTime = (dateString) => {
        if (!dateString)
            return "—";
        const date = new Date(dateString);
        const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
        const month = date.toLocaleDateString("en-US", { month: "short" });
        const day = date.getDate();
        const time = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
        return `${dayName}, ${month} ${day}, ${time}`;
    };
    const enhancedInteractions = useMemo(() => {
        return interactions.map((interaction) => ({
            ...interaction,
            formattedDate: formatDateTime(interaction.InteractionStartDateUtc),
            sortableDate: interaction.InteractionStartDateUtc ? new Date(interaction.InteractionStartDateUtc).getTime() : 0,
        }));
    }, [interactions]);
    const columns = useMemo(() => [
        {
            key: "sortableDate",
            label: "Date",
            sortable: true,
            align: "left",
            width: "200px",
            render: (_, row) => row.formattedDate,
        },
        {
            key: "ToObjectName",
            label: "To",
            sortable: true,
            align: "left",
            width: "250px",
            render: (value) => value || "Unknown Recipient",
        },
        {
            key: "InteractionFinalAction",
            label: "Outcome",
            sortable: true,
            align: "left",
            width: "150px",
            render: (value) => value || "—",
        },
        {
            key: "speedToCare",
            label: "Speed to Care",
            sortable: false,
            align: "center",
            width: "250px",
            render: (_, row) => (_jsx(SpeedToCare, { interaction: row, compact: true, showLegend: false, hideGraph: false })),
        },
        {
            key: "actions",
            label: "Actions",
            sortable: false,
            align: "center",
            width: "120px",
            render: (_, row) => (_jsxs("div", { style: {
                    display: "flex",
                    gap: "4px",
                    justifyContent: "center",
                }, children: [_jsx("button", { onClick: () => handleViewInteractionDetail(row), style: {
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#374B94",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            transition: "background-color 0.2s",
                        }, onMouseEnter: (e) => {
                            e.currentTarget.style.backgroundColor = "#f0f4ff";
                        }, onMouseLeave: (e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                        }, title: "View interaction details", children: _jsx(FontAwesomeIcon, { icon: faEye, size: "sm" }) }), _jsx("button", { onClick: () => handleViewCallFlowAnalysis(row), style: {
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#28a745",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            transition: "background-color 0.2s",
                        }, onMouseEnter: (e) => {
                            e.currentTarget.style.backgroundColor = "#f0fff4";
                        }, onMouseLeave: (e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                        }, title: "Analyze call flow", children: _jsx(FontAwesomeIcon, { icon: faChartLine, size: "sm" }) })] })),
        },
    ], []);
    if (loading && interactions.length === 0) {
        return (_jsx("div", { className: `call-finder-results-container ${className}`, style: { padding: "0" }, children: _jsx("div", { className: "d-flex justify-content-center align-items-center flex-column", style: {
                    height: "200px",
                    gap: "12px",
                }, children: _jsx("div", { className: "text-muted", style: { fontSize: "18px" }, children: "Loading interactions..." }) }) }));
    }
    return (_jsxs("div", { className: `call-finder-results-container ${className}`, children: [enhancedInteractions.length === 0 ? (_jsx("div", { className: "text-center text-muted p-40", style: { fontSize: "16px" }, children: "No interactions found" })) : (_jsxs(_Fragment, { children: [_jsx(SortableDataTable, { columns: columns, data: enhancedInteractions, defaultSortKey: "sortableDate", defaultSortDirection: "asc", emptyMessage: "No interactions found", getRowId: (row) => row.Id }), showLoadMore && onLoadMore && (_jsxs("div", { style: {
                            padding: "20px",
                            borderTop: "1px solid var(--ps-gray4)",
                            backgroundColor: "#f8f9fa",
                            display: "flex",
                            justifyContent: "center",
                            gap: "12px",
                            marginTop: "0",
                        }, children: [_jsx("button", { onClick: () => onLoadMore(100), disabled: isLoadingMore, style: {
                                    padding: "10px 24px",
                                    backgroundColor: "white",
                                    color: "#374B94",
                                    border: "1px solid #374B94",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    cursor: isLoadingMore ? "not-allowed" : "pointer",
                                    opacity: isLoadingMore ? 0.6 : 1,
                                    transition: "all 0.2s",
                                }, onMouseEnter: (e) => {
                                    if (!isLoadingMore) {
                                        e.currentTarget.style.backgroundColor = "#374B94";
                                        e.currentTarget.style.color = "white";
                                    }
                                }, onMouseLeave: (e) => {
                                    if (!isLoadingMore) {
                                        e.currentTarget.style.backgroundColor = "white";
                                        e.currentTarget.style.color = "#374B94";
                                    }
                                }, children: isLoadingMore ? "Loading..." : "Load More 100" }), _jsx("button", { onClick: () => onLoadMore(1000), disabled: isLoadingMore, style: {
                                    padding: "10px 24px",
                                    backgroundColor: "white",
                                    color: "#374B94",
                                    border: "1px solid #374B94",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    cursor: isLoadingMore ? "not-allowed" : "pointer",
                                    opacity: isLoadingMore ? 0.6 : 1,
                                    transition: "all 0.2s",
                                }, onMouseEnter: (e) => {
                                    if (!isLoadingMore) {
                                        e.currentTarget.style.backgroundColor = "#374B94";
                                        e.currentTarget.style.color = "white";
                                    }
                                }, onMouseLeave: (e) => {
                                    if (!isLoadingMore) {
                                        e.currentTarget.style.backgroundColor = "white";
                                        e.currentTarget.style.color = "#374B94";
                                    }
                                }, children: isLoadingMore ? "Loading..." : "Load More 1000" })] }))] })), _jsx(InteractionDetailModal, { interaction: selectedInteraction, open: showDetailModal, onClose: handleCloseDetailModal }), _jsx(CallFlowAnalysisModal, { interaction: selectedAnalysisInteraction, open: showAnalysisModal, onClose: handleCloseAnalysisModal })] }));
};
export default CallFinderResults;
//# sourceMappingURL=CallFinderResults.js.map